/* eslint-disable max-lines */
/**
 * Legacy `IS_*` flag declarations — the app-side implementation home behind
 * the `@/features/flags` barrel (P3 · monorepo-wave-1-p3-feature-flags · Z2;
 * relocated from `src/components/constants/flags.ts`).
 *
 * Consumers import `@/features/flags` — never this module directly. Tests
 * that need to stub individual `IS_*` declarations `jest.mock` THIS leaf
 * module (`@/features/flags/legacy-flags`): the barrel star-re-exports it, so
 * a leaf mock intercepts barrel consumers while the barrel's own hooks stay
 * real — the same interception scope the old path had.
 */
import { sessionlessEvidenceAwarenessDevVariantOverrideAtom } from '@/atoms/sessionlessEvidenceAwarenessDevAtoms';
import { isAgentHomeV1Enabled } from '@/features/flags/agent-home';
import { FEATURE_FLAG_DOMAIN } from '@/features/flags/constants';
import { isEnglishMarket } from '@/features/flags/market';
import { posthog } from '@/features/flags/posthog-compat';
import { isProblemBasedChartingEnabled } from '@/features/flags/problem-based-charting-flag';
import { isTauri } from '@/tauri/utils';
import { type CountryConfig, CountryConfigSchema } from '@/types/country-config';
import { isOnPrem } from '@/utils/buildProfile';
import { logger } from '@/utils/logger/logger';
import { ENV_NAME, getEnvironment } from '@heidi/api/config';
import { EHRProvider, Region, SignupSource } from '@heidi/schema/ml-scribe';
// The new-session-list key comes off the descriptor that owns it. Ruling 36's
// whole-class fan-out moved that flag into `@heidi/session/flags`, so the two
// literals the dual-provider gates below used to spell were second minting
// sites; `./flags` is headless, so binding it costs this module nothing.
import { newSessionListFlag } from '@heidi/session/flags';
import { OpenFeature, ProviderStatus } from '@openfeature/react-sdk';
import { getDefaultStore } from 'jotai';
import { isMobile } from 'react-device-detect';
import { z } from 'zod';

// Note: For development - set via NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS env var
const FORCE_ENABLE_ALL_FEATURE_FLAGS =
  process.env.NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS === 'true';
if (FORCE_ENABLE_ALL_FEATURE_FLAGS) {
  const ph = posthog as typeof posthog & {
    isFeatureEnabled: () => boolean;
    getFeatureFlag: () => string;
  };
  ph.isFeatureEnabled = () => true;
  ph.getFeatureFlag = () => 'variant-1';
}

// Adding a new feature flag below? Please also drop a one-liner into
// `src/features/flags-dev/descriptions.ts` — it powers the Dev Tools
// Flags panel so others can see what the flag does without grepping
// the calling code. Optional; the panel gracefully omits the line
// when no description is set.

export const IS_CIBA_LOGIN_ENABLED = () => {
  return OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getBooleanValue(
    'release-login-with-heidi-app',
    false
  );
};

// Regional signup routing (Flagsmith JSON flag `config-regional-signup`) lives
// in `@/lists/regionalSignup` — see `GET_REGIONAL_SIGNUP_MAP` there. It is kept
// out of this module because `countriesToRegion` reads it on every lookup and
// this module is partially `jest.mock`ed across the suite.

export const IS_BRANDED_EXPORTED_DOCUMENTS_ENABLED = () => {
  return posthog.isFeatureEnabled('release-branded-exported-documents');
};

export const IS_CUSTOM_EXPORT_HEADER_FOOTER_ENABLED = () => {
  return posthog.isFeatureEnabled('release-custom-export-header-footer');
};

export const IS_TEAM_EXPORT_HEADER_FOOTER_ENABLED = () => {
  return posthog.isFeatureEnabled('release-team-export-header-footer');
};

export const IS_LIVE_TRANSCRIPTS_ENABLED = () => {
  // On-prem serves the live-transcript endpoint from ml-scribe itself
  // (uploaded audio → whisper → GET /sessions/:id/transcript/live, which
  // returns the user_uploaded transcript). Use the live pane rather than the
  // legacy fetch-only path so on-prem gets the richer transcript UI.
  if (isOnPrem) {
    return true;
  }
  if (process.env.NEXT_PUBLIC_IS_TAURI) {
    return false;
  }
  if (USE_RECORDING_V2()) {
    return true;
  }
  const isGeminiTranscriber =
    posthog.getFeatureFlag('config-default-transcriber') === 'Gemini';
  return (
    (isGeminiTranscriber || IS_CHUNK_RECORDING_ENABLED()) &&
    posthog.isFeatureEnabled('release-live-transcripts')
  );
};

// Community template likes and comments
export const IS_COMMUNITY_LIKES_ENABLED = () => {
  return posthog.isFeatureEnabled('release-community-likes') ?? false;
};

export const IS_BLOCK_EDITOR_V1_ENABLED = () => {
  return posthog.isFeatureEnabled('release-block-editor-v1') ?? false;
};

export const IS_LIVE_TRANSCRIPTION_INDICATOR_ENABLED = () => {
  return posthog.isFeatureEnabled('release-live-transcription-indicator');
};

// flag to open up Pyne for Germany
export const IS_PYNE_AVATAR = () => {
  if (posthog.isFeatureEnabled('pyne-change-onboarding-germany')) return 'germany';
  if (posthog.isFeatureEnabled('pyne-change-onboarding')) return 'global';
  return null;
};

export const IS_HIDE_TAURI_UPDATER_ENABLED = () => {
  return posthog.isFeatureEnabled('hide-tauri-updater');
};

/**
 * Single source of truth for the region-aware desktop updater flag key
 * (APP-6113 §5.19). Shared by the static `getRegionalUpdaterGate` (below) and
 * the `useRegionalUpdaterGate` hook in `@/features/flags`.
 */
export const REGIONAL_UPDATER_FLAG_KEY = 'release-apps-regional-updater-desktop';

export type RegionalUpdaterGate = 'on' | 'off' | 'unknown';

/**
 * APP-6113 — region-aware desktop updater gate (parent APP-6111 §5.18/§5.19).
 *
 * Flagsmith (OpenFeature) flag `REGIONAL_UPDATER_FLAG_KEY`. Gates
 * ONLY the updater **behaviour change** (Rust `check/install_update_for_region`
 * + region→channel routing + the fail-closed region gate). Does NOT gate
 * `build_id` / telemetry / SBOM.
 *
 * Returns a **tri-state, never a bare boolean**, because "flag resolved to
 * false" and "provider not ready yet" must be handled differently:
 *  - `'on'`      → provider READY + flag true  → region-aware updater.
 *  - `'off'`     → provider READY + flag false → today's global updater.
 *  - `'unknown'` → provider NOT READY (startup race / error / stale) ⇒ callers
 *                  do **no update activity**. Treating not-ready as `'off'`
 *                  would run the GLOBAL feed during the startup window and could
 *                  serve a UK/EU client an uncertified `stable` version *before*
 *                  the flag resolves to ON during the freeze (DC3 fail-closed).
 *                  Self-heals: consumers re-check once the provider is READY.
 *
 * ⚠️ OPERATIONAL CONSTRAINT (§5.18) — this flag MUST be a **global kill
 * switch**. Do NOT give it region targeting or per-region staged rollout in
 * Flagsmith: the OpenFeature `region` evaluation trait is sourced from
 * `useRegion()` (`openfeature-context-sync.tsx`), which fails open to AU and
 * honours `?region=` (C10) — region-targeting this flag would let a spoofed
 * region flip a UK/EU client to OFF and onto the global feed. Region isolation
 * is enforced in-code (`kindeProperties.region`, fail-closed), never by the flag.
 *  - OFF (global): safe during prep; instant revert to today's updater.
 *  - ON: region-aware; **must be ON before the UK/EU cert freeze.**
 *  - Turning OFF for UK/EU *after* the freeze is an EMERGENCY change.
 *  - on-prem: N/A (no Flagsmith; updater stripped).
 *
 * Static (non-hook) form for the rematch effect (`tauriUpdateModel.ts`). Inside
 * React components use `useRegionalUpdaterGate` from `@/features/flags` so the
 * gate re-evaluates when the provider becomes ready after mount.
 */
export const getRegionalUpdaterGate = (): RegionalUpdaterGate => {
  const client = OpenFeature.getClient(FEATURE_FLAG_DOMAIN);
  if (client.providerStatus !== ProviderStatus.READY) return 'unknown';
  return client.getBooleanValue(REGIONAL_UPDATER_FLAG_KEY, false) ? 'on' : 'off';
};

export const IS_MIND_BODY_INTEGRATION_ENABLED = () => {
  return posthog.isFeatureEnabled('release-mindbody-integration');
};

export const IS_VERADIGM_INTEGRATION_ENABLED = () => {
  return posthog.isFeatureEnabled('release-veradigm-integration');
};

export const IS_EPIC_INTEGRATION_ENABLED = () => {
  return posthog.isFeatureEnabled('release-epic-integration');
};

// Person-keyed cohorts: read via `useIsDisablePushToEpic`, since an imperative
// read freezes the pre-identify false.
export const DISABLE_PUSH_TO_EPIC_FLAG_KEY = 'disable-push-to-epic';

export const IS_ENABLE_PUSH_TO_EPIC_SECTIONAL = () => {
  return posthog.isFeatureEnabled('enable-push-to-epic-sectional');
};

export const IS_TAB_RENAME_ENABLED = () => {
  return posthog.isFeatureEnabled('release-tab-rename');
};

export const IS_LESS_DEBOUNCED_NOTE_DOCUMENT_UPDATE_ENABLED = () => {
  return posthog.isFeatureEnabled('release-less-debounced-note-document-update');
};

export const IS_CLINICAL_CODING_ENABLED = () => {
  return posthog.isFeatureEnabled('release-clinical-coding');
};

export const IS_CLINICAL_TASKS_ENABLED = () => {
  return posthog.isFeatureEnabled('release-clinical-tasks') ?? false;
};

export const IS_ENTITY_VERIFICATION_ENABLED = () => {
  return posthog.isFeatureEnabled('release-entity-verification') ?? false;
};

/** PostHog multivariate — auto-mode is enabled when the variant starts with AUTO_. */
export const IS_HEIDI_VERIFY_AUTO_MODE_ENABLED = () => {
  const variant = posthog.getFeatureFlag('release-entity-verification');
  return typeof variant === 'string' && variant.startsWith('AUTO_');
};

const RELEASE_MSH_CLINICAL_CODING_FLAG_KEY = 'release-msh-clinical-coding';

/**
 * Legacy alias. The key/default pair and the read itself now live in
 * `@/features/flags/problem-based-charting-flag`; this name stays because its
 * callers - and their `jest.mock('@/components/constants/flags')` literals -
 * spell it, and those mocks would stop intercepting if the export moved.
 */
export const IS_PROBLEM_BASED_CHARTING_ENABLED = isProblemBasedChartingEnabled;

export const IS_CLINICAL_CODING_DASHBOARD_ENABLED = () => {
  return OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getBooleanValue(
    RELEASE_MSH_CLINICAL_CODING_FLAG_KEY,
    false
  );
};

export const IS_CLINICAL_TASKS_GENERATE_DOCUMENT_ENABLED = () => {
  return posthog.isFeatureEnabled('release-clinical-tasks-generate-document');
};

/**
 * GRO-2846: Displays the Evidence search bar in a central location
 * within the empty note area during transcription.
 *
 * Non-hook variant for usage outside React components/hooks.
 */
export const IS_EVIDENCE_CENTRAL_SEARCH_BAR_ENABLED = () => {
  return posthog.isFeatureEnabled('experiment-evidence-central-search-bar') ?? false;
};

/**
 * PostHog multivariate experiment: in-session document drafting from clinical tasks.
 * - control: off
 * - banner: carousel banner above input
 * - chips: document suggestions merged into the suggestions peek sheet as chips
 */
export type EvidenceDocumentSuggestionExperimentVariant = 'control' | 'banner' | 'chips';

export const GET_EVIDENCE_DOCUMENT_SUGGESTION_EXPERIMENT_VARIANT =
  (): EvidenceDocumentSuggestionExperimentVariant => {
    const schema = z.enum(['control', 'banner', 'chips']).catch('control');
    return schema.parse(posthog.getFeatureFlag('exp-evidence-document-suggestion'));
  };

/**
 * GRO-3264 / GRO-3265 / GRO-3281: Flagsmith (OpenFeature) multivariate A/B/C
 * experiment for the post-transcription note-entry surface — what happens the
 * first time a clinician would choose a template after recording stops.
 * - control: current behavior (full picker / existing post-stop logic)
 * - auto_generate: auto-generate in Auto mode, no picker (Variant A / GRO-3264)
 * - simple_picker: stripped-down picker — search + Auto + suggestions + saved
 *   templates + free-text Create, no upgrade/create-template/filter chrome
 *   (Variant B / GRO-3265)
 * - auto_first_picker: minimal Auto-first picker — Auto-only until typing, no
 *   saved templates list (Variant C / GRO-3281)
 *
 * Enrolment only changes behavior for users who would otherwise land on an
 * Auto note or the full picker: a specific (non-Auto) saved default template
 * still takes precedence, and such users stream their default before the
 * experiment block runs (so they get no `$exposure`). An explicit per-session
 * template/Auto selection is likewise respected (all handled upstream in
 * `_actionPostRecording`).
 *
 * Flagsmith flag shape: the flag's enabled state is enrolment (read via
 * {@link IS_POST_TRANSCRIPTION_NOTE_ENTRY_ASSIGNED}); its string value is the
 * variant (read via {@link GET_POST_TRANSCRIPTION_NOTE_ENTRY_VARIANT}).
 */
export type PostTranscriptionNoteEntryVariant =
  | 'control'
  | 'auto_generate'
  | 'simple_picker'
  | 'auto_first_picker';

export const POST_TRANSCRIPTION_NOTE_ENTRY_FLAG_KEY =
  'release-growth-post-transcription-note-entry';

export const GET_POST_TRANSCRIPTION_NOTE_ENTRY_VARIANT =
  (): PostTranscriptionNoteEntryVariant => {
    const schema = z
      .enum(['control', 'auto_generate', 'simple_picker', 'auto_first_picker'])
      .catch('control');
    return schema.parse(
      OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getStringValue(
        POST_TRANSCRIPTION_NOTE_ENTRY_FLAG_KEY,
        'control'
      )
    );
  };

/**
 * Whether the user is enrolled in the post-transcription note-entry experiment
 * (the Flagsmith flag is enabled for them). Used to gate manual exposure so we
 * only record exposure for assigned users; non-enrolled users fall through to
 * the unchanged control behavior with no exposure event.
 */
export const IS_POST_TRANSCRIPTION_NOTE_ENTRY_ASSIGNED = (): boolean => {
  return OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getBooleanValue(
    POST_TRANSCRIPTION_NOTE_ENTRY_FLAG_KEY,
    false
  );
};

/**
 * GRO-3266: side-by-side note preference experiment. Runs through the existing
 * PostHog `config-alternative-generation-v2` flag (the single flag that drives
 * every backend alternative-generation experiment) instead of a dedicated flag,
 * so the whole side-by-side workflow stays in one place.
 *
 * The `SBS_PREF` variant is the treatment arm: ml-scribe returns a second
 * `model_comparison` note (no upsell) and the FE shows the side-by-side
 * comparison for both template and Auto-mode notes. Every other value — incl.
 * the PostHog `control` holdout and unenrolled users — keeps the single-note
 * flow. Reading the flag here also emits PostHog's native `$feature_flag_called`
 * exposure, so no manual Amplitude exposure is required.
 */
export const SIDE_BY_SIDE_NOTE_PREFERENCE_VARIANT_KEY = 'SBS_PREF';

export const IS_SIDE_BY_SIDE_NOTE_PREFERENCE_TREATMENT = (): boolean =>
  posthog.getFeatureFlag('config-alternative-generation-v2') ===
  SIDE_BY_SIDE_NOTE_PREFERENCE_VARIANT_KEY;

/**
 * Whether the user is in the GRO-3266 model-comparison experiment (control or
 * SBS_PREF treatment). Unenrolled users and other flag variants are excluded.
 */
export const IS_IN_SIDE_BY_SIDE_NOTE_PREFERENCE_EXPERIMENT = (): boolean => {
  const variant = posthog.getFeatureFlag('config-alternative-generation-v2');
  return variant === 'control' || variant === SIDE_BY_SIDE_NOTE_PREFERENCE_VARIANT_KEY;
};

/**
 * Gates the Evidence CTA on document-category clinical tasks. When on,
 * the task row shows a FileText-glyphed Evidence button that opens the
 * in-session Evidence chat sidebar and auto-submits the task title
 * (with the source_quote as context) so Heidi drafts the document using
 * the consultation transcript as evidence.
 */
export const IS_CLINICAL_TASKS_EVIDENCE_CTA_ENABLED = () => {
  return posthog.isFeatureEnabled('release-clinical-tasks-evidence-cta') ?? false;
};

/** PostHog: gates doc export suggestions in Evidence using attached evidence. */
export const IS_EVIDENCE_DOC_EXPORT_SUGGESTIONS_ENABLED = () => {
  // FLAG_CLEANUP: hardcoded false because the PostHog flag is no longer configured.
  return false;
};

export const IS_TASK_GENERATION_BG_AUTOGEN_ENABLED = () => {
  return posthog.isFeatureEnabled('release-task-generation-bg-autogen') ?? false;
};

export const IS_CALLS_ENABLED = () => {
  return posthog.isFeatureEnabled('release-calls');
};

/** Comms Inbox tab inside `/comms`. Gated separately so it can roll out independently. */
export const IS_COMMS_INBOX_ENABLED = () => {
  return posthog.isFeatureEnabled('release-comms-inbox') ?? false;
};

/** Comms Patients tab inside `/comms`. Gated separately so it can roll out independently. */
export const IS_COMMS_PATIENTS_ENABLED = () => {
  return posthog.isFeatureEnabled('release-comms-patients') ?? false;
};

/** Comms Reminders tab inside `/comms`. Gated separately so it can roll out independently. */
export const IS_COMMS_REMINDERS_ENABLED = () => {
  return posthog.isFeatureEnabled('release-comms-reminders') ?? false;
};

/**
 * Calls live refresh: enables 15s background polling of the calls table and
 * the "X new calls" refresh pill. Gated separately from {@link IS_CALLS_ENABLED}
 * so we can roll out — and quickly disable — the polling/pill behaviour without
 * affecting the rest of the calls feature.
 */
export const IS_CALLS_LIVE_REFRESH_ENABLED = () => {
  return posthog.isFeatureEnabled('release-calls-live-refresh') ?? false;
};

export const IS_PROTOCOLS_ENABLED = () => {
  return posthog.isFeatureEnabled('release-protocols');
};

export const IS_COMMS_SELF_SERVE_ENABLED = () => {
  return posthog.isFeatureEnabled('release-comms-self-serve');
};

/** Comms guardrails v2: LLM user_message + inline blocking UI (DES-1517). */
export const IS_COMMS_GUARDRAILS_V2_ENABLED = () => {
  // FLAG_CLEANUP: hardcoded false because the PostHog flag is no longer configured.
  return false;
};

export const IS_RICH_EDITOR_ENABLED = () => {
  return posthog.isFeatureEnabled('release-rich-editor');
};

export const IS_BBE_CONTEXT_TAB_ENABLED = () => {
  return posthog.isFeatureEnabled('release-bbe-context-tab');
};

export const IS_SSO_MICROSOFT_LOGOUT_ENABLED = () => {
  return posthog.isFeatureEnabled('release-sso-microsoft-logout');
};

export const IS_CHUNK_RECORDING_ENABLED = () => {
  return posthog.isFeatureEnabled('release-chunk-recording');
};

export const IS_DEBUG_CHUNKS_ENABLED = () => {
  return posthog.isFeatureEnabled('debug-chunks');
};

export const IS_EXTRA_TEAM_SETTINGS_CONFIG_ENABLED = () => {
  return posthog.isFeatureEnabled('release_extra_team_settings_config');
};

export const IS_PRECHART_ENABLED = () => {
  return posthog.isFeatureEnabled('release-prechart');
};

export const IS_PRECHART_INTERNAL_ADMIN_ENABLED = () => {
  return posthog.isFeatureEnabled('prechart-internal-admin');
};

export const IS_ENABLE_FULL_RICH_TEXT_ENABLED = () => {
  return posthog.isFeatureEnabled('enable-full-rich-text');
};

export const USE_RECORDING_V2 = () => {
  if (isTauri()) return false;
  return true;
};

export const IS_INTEGRATION_MARKET_ENABLED = () => {
  return posthog.isFeatureEnabled('release-integration-market');
};

export const IS_DICTATION_MOBILE_ENABLED = () => {
  return posthog.isFeatureEnabled('release-dictation-mobile');
};

export const IS_TEMPLATE_FOOTER_ENABLED = () => {
  return posthog.isFeatureEnabled('release-template-footer');
};

// This should not be cleaned up because desktop does not support tab-dictation yet
export const IS_TAB_DICTATION_ENABLED = () => {
  return posthog.isFeatureEnabled('release-tab-dictation');
};

export const IS_STOP_RECORDING_AT_5_HOURS_ENABLED = () => {
  // FLAG_CLEANUP: hardcoded false because the PostHog flag is no longer configured.
  return false;
};

export const IS_SELF_ONBOARDING_MODAL_QUEUED = () => {
  // FLAG_CLEANUP: hardcoded false because the PostHog flag is no longer configured.
  return false;
};

export const IS_OPTIMITCALLY_CREATE_EMPTY_DOCUMENT_ENABLED = () => {
  return posthog.isFeatureEnabled('release-optimistically-create-empty-document');
};

export const IS_STEP_LOADING_ENABLED = () => {
  return posthog.isFeatureEnabled('release-step-loading-progression');
};

export const IS_MD_WIDGET_LAUNCH_ENABLED = () => {
  return posthog.isFeatureEnabled('release-md-widget-launch');
};

export const IS_DEBUG_MARKETPLACE_ENABLED = () => {
  return posthog.isFeatureEnabled('debug-marketplace') ?? false;
};

/**
 * Hide Email Functionality: When enabled, hides all email-related features
 * in the application (e.g., email sharing, email notifications UI).
 */
export const IS_HIDE_EMAIL_FUNCTIONALITY_ENABLED = () => {
  return posthog.isFeatureEnabled('release-hide-email-functionality');
};

export const IS_HEIDI_LEARNS_ENABLED = () => {
  return posthog.isFeatureEnabled('release-heidi-learns');
};

/**
 * GRO-3284: Flagsmith (OpenFeature) multivariate A/B/C experiment for the
 * post-note quality experience — how a clinician is coached/rewarded after a
 * note is generated. Replaces the standalone `release-growth-post-note-quality-panel`
 * boolean: the coaching sidebar (GRO-3268/3269/3270) is now Arms B/C of this test.
 * - control: existing gamified trial only, no coaching sidebar.
 * - sidebar_only: coaching sidebar only; gamified trial suppressed. Completing
 *   2 sidebar tasks grants +7 trial days, all 5 grants +7 more (=+14).
 * - sequential: coaching sidebar first (+7/+7); once all sidebar tasks are
 *   complete the gamified trial is introduced (its own +14 by existing logic).
 *
 * Flag shape: enabled state is enrolment ({@link IS_POST_NOTE_QUALITY_EXPERIMENT_ASSIGNED});
 * the string value is the variant ({@link GET_POST_NOTE_QUALITY_EXPERIMENT_VARIANT}).
 */
export type PostNoteQualityExperimentVariant = 'control' | 'sidebar_only' | 'sequential';

export const POST_NOTE_QUALITY_EXPERIMENT_FLAG_KEY =
  'release-growth-post-note-quality-experience';

export const GET_POST_NOTE_QUALITY_EXPERIMENT_VARIANT =
  (): PostNoteQualityExperimentVariant => {
    const schema = z.enum(['control', 'sidebar_only', 'sequential']).catch('control');
    return schema.parse(
      OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getStringValue(
        POST_NOTE_QUALITY_EXPERIMENT_FLAG_KEY,
        'control'
      )
    );
  };

/**
 * Whether the user is enrolled in the post-note quality experiment (the
 * Flagsmith flag is enabled for them). Gates manual exposure so only assigned
 * users record `$exposure`, and gates the arm-specific behaviour below.
 */
export const IS_POST_NOTE_QUALITY_EXPERIMENT_ASSIGNED = (): boolean => {
  return OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getBooleanValue(
    POST_NOTE_QUALITY_EXPERIMENT_FLAG_KEY,
    false
  );
};

/**
 * GRO-3268 "Improve your notes" coaching sidebar visibility. Shown for the
 * `sidebar_only` and `sequential` arms of the post-note quality experiment
 * (GRO-3284). The session page surfaces a right-docked checklist of
 * note-improvement tasks after a note is generated.
 */
export const IS_POST_NOTE_QUALITY_PANEL_ENABLED = () => {
  if (!IS_POST_NOTE_QUALITY_EXPERIMENT_ASSIGNED()) return false;
  const variant = GET_POST_NOTE_QUALITY_EXPERIMENT_VARIANT();
  return variant === 'sidebar_only' || variant === 'sequential';
};

export const IS_CREATE_TEMPLATE_MODAL_VIA_CHAT_ENABLED = () => {
  return posthog.isFeatureEnabled('release-create-template-modal-via-chat');
};

export const IS_SIDE_BY_SIDE_NOTE_GEN_ENABLED = () => {
  return posthog.isFeatureEnabled('release-side-by-side-note-gen') ?? false;
};

export const IS_SIDE_BY_SIDE_TIMINGS_ENABLED = () => {
  return posthog.isFeatureEnabled('release-side-by-side-note-gen-timings') ?? false;
};

export const IS_REMOVE_DEMO_OPTION_ENABLED = () => {
  return posthog.isFeatureEnabled('release-remove-demo-option');
};
// Header copy variants: control + variant-1 ... variant-4
export const SIGN_UP_MODAL_HEADER_COPY_VARIANTS = () => {
  const schema = z
    .enum(['control', 'variant-1', 'variant-2', 'variant-3', 'variant-4'])
    .catch('control');
  return schema.parse(posthog.getFeatureFlag('release-sign-up-modal-header-variants'));
};

// Description copy variants: control + variant-1 ... variant-4
export const SIGN_UP_MODAL_DESCRIPTION_COPY_VARIANTS = () => {
  const schema = z
    .enum(['control', 'variant-1', 'variant-2', 'variant-3', 'variant-4'])
    .catch('control');
  return schema.parse(
    posthog.getFeatureFlag('release-sign-up-modal-description-variants')
  );
};

// Logo: control or removed
export const SIGN_UP_MODAL_LOGO_VARIANTS = () => {
  const schema = z.enum(['control', 'removed']).catch('control');
  return schema.parse(posthog.getFeatureFlag('release-sign-up-modal-logo-variants'));
};

// Login link: control or removed
export const SIGN_UP_MODAL_LOGIN_LINK_VARIANTS = () => {
  const schema = z.enum(['control', 'removed']).catch('control');
  return schema.parse(
    posthog.getFeatureFlag('release-sign-up-modal-login-link-variants')
  );
};

export const IS_FORCE_EN_ONLY_ENABLED = () => {
  return posthog.isFeatureEnabled('force-en-only') ?? false;
};

/**
 * When on, team signups (onboarding organisation size > "Just me") are offered
 * the Practice trial modal instead of the Clinician trial modal at onboarding.
 * Off → every signup gets the Clinician trial (prior behaviour). Rollout gate,
 * not an experiment.
 */
export const IS_PRACTICE_INVITE_FLOW_ENABLED = () => {
  return posthog.isFeatureEnabled('release-practice-signup-flow');
};

/** Shipped 100% — PostHog `release-pinned-template-auto-generation`. */
export const IS_TEMPLATE_PINNING_ENABLED = () => true;

export const IS_PATIENT_AUTO_LINKING_ENABLED = () => {
  return posthog.isFeatureEnabled('release-patient-auto-linking');
};

export const IS_TEAM_ANALYTICS_ENABLED = () => {
  return posthog.isFeatureEnabled('team-analytics-dashboard');
};

export const IS_ORG_ANALYTICS_ENABLED = () => {
  return posthog.isFeatureEnabled('org-analytics-dashboard');
};

export const USE_TRANSCRIPTION_SERVICE_V2 = () => {
  return posthog.isFeatureEnabled('use-transcription-service-v2') ?? false;
};

export const IS_EHR_LAUNCH_PAST_NOTES_AS_CONTEXT_ENABLED = () => {
  return posthog.isFeatureEnabled('release-ehr-launch-past-note-as-context') ?? false;
};

export const IS_UPCOMING_SESSIONS_ENABLED = () => {
  return posthog.isFeatureEnabled('release-upload-upcoming-sessions');
};

export const IS_CONSOLIDATED_SESSIONS_LIST_ENABLED = () => {
  return posthog.isFeatureEnabled('release-consolidated-sessions-list');
};

export const IS_BINARY_CSAT_ENABLED = () => {
  return posthog.isFeatureEnabled('release-binary-csat') ?? false;
};

/** Shipped 100% — PostHog `release-specialty-in-organisation-survey`. UI call sites untouched (multi-pod). */
export const IS_SPECIALTY_IN_ORGANISATION_SURVEY_ENABLED = () => {
  return true;
};

export const IS_EHR_QUESTION_IN_ORGANISATION_SURVEY_ENABLED = () => {
  return posthog.isFeatureEnabled('release-ehr-question-in-organisation-survey');
};

/** @deprecated Use getPhoneNumberVariantForCountry instead */
export const GET_PHONE_NUMBER_IN_SIGNUP_US_VARIANT = ():
  | 'hidden'
  | 'required'
  | 'optional' => {
  const schema = z.enum(['hidden', 'required', 'optional']).catch('hidden');
  return schema.parse(posthog.getFeatureFlag('release-phone-number-in-signup-us'));
};

export const GET_COUNTRY_CONFIG = (): CountryConfig | null => {
  try {
    let flagPayload = posthog.getFeatureFlagPayload('country-config');
    if (!flagPayload) return null;

    // PostHog may return the payload as a JSON string instead of a parsed object
    if (typeof flagPayload === 'string') {
      try {
        flagPayload = JSON.parse(flagPayload) as typeof flagPayload;
      } catch {
        logger.error('Failed to parse country config payload string', {
          payload: flagPayload,
        });
        return null;
      }
    }

    const parseResult = CountryConfigSchema.safeParse(flagPayload);
    if (!parseResult.success) {
      logger.error('Invalid country config payload from PostHog', {
        error: parseResult.error,
        payload: flagPayload,
      });
      return null;
    }
    return parseResult.data;
  } catch (error) {
    logger.error('Error fetching country config from PostHog', { error });
    return null;
  }
};

/** @deprecated Replaced by `<DiscoveryCallNavItem />`, which derives visibility
 *  from `country-config.discoveryCallLink`. Remove in the cleanup PR. */
export const IS_ANDRES_CTA = () => {
  return posthog.isFeatureEnabled('release-german-book-call');
};

/** @deprecated Replaced by `<DiscoveryCallNavItem />`, which derives visibility
 *  from `country-config.discoveryCallLink`. Remove in the cleanup PR. */
export const IS_FRANCE_CTA = () => {
  return posthog.isFeatureEnabled('release-france-book-call');
};

/** @deprecated Replaced by `<DiscoveryCallNavItem />`, which derives visibility
 *  from `country-config.discoveryCallLink`. Remove in the cleanup PR. */
export const IS_SPANISH_CTA = () => {
  return posthog.isFeatureEnabled('release-spanish-book-call');
};

export const IS_GERMAN_GET_STARTED_CTA = () => {
  return posthog.isFeatureEnabled('release-german-get-started-cta');
};

export const IS_ONBOARDING_COMPLIANCE_US_ENABLED = () => {
  return posthog.isFeatureEnabled('release-onboarding-compliance-us');
};

export const IS_ONBOARDING_COMPLIANCE_DE_ENABLED = () => {
  return posthog.isFeatureEnabled('release-onboarding-compliance-de');
};

export const IS_DESKTOP_AUTH0_AUTH_ENABLED = () => {
  if (!isTauri()) {
    return false;
  }
  return posthog.isFeatureEnabled('desktop-auth0-auth');
};

/**
 * Desktop Logout-on-Exit: When enabled, the desktop app will automatically
 * logout the user when the app is closed. This is intended for enterprise
 * users on shared devices where automatic session persistence is a security concern.
 *
 * Only applies when using Auth0 Desktop authentication.
 */
export const IS_DESKTOP_LOGOUT_ON_EXIT_ENABLED = () => {
  if (!isTauri()) {
    return false;
  }
  return posthog.isFeatureEnabled('desktop-auth0-logout-on-exit');
};

export const IS_ASK_HEIDI_V2_CHAT_COMPLETIONS_ENABLED = () => {
  return posthog.isFeatureEnabled('release-ask-heidi-v2-chat-completions') ?? false;
};

export const IS_FORMS_COMMUNITY_PAGE_ENABLED = () => {
  return posthog.isFeatureEnabled('release-forms-community-page');
};

export const IS_RELEASE_FORM_FILLING_FLOW_ALIGNMENT_ENABLED = () => {
  // FLAG_CLEANUP: hardcoded false because the PostHog flag is no longer configured.
  return false;
};

export const IS_WEB_TAB_SHARING_ENABLED = () => {
  // Web-only: desktop uses native capture, not browser tab sharing.
  if (isTauri()) return false;
  return true;
};

export const IS_HIDE_PRO_MONTHLY_OPTION = () => {
  return posthog.isFeatureEnabled('release-hide-pro-monthly-option');
};

export const IS_REFINED_COPY_ENABLED = () => {
  return posthog.isFeatureEnabled('release-refined-copy');
};

/**
 * Release gate for the desktop native clipboard rescue fallback. Fail-closed:
 * flag missing or PostHog unreachable → rescue stays off. Disabling the flag
 * in PostHog is the instant fleet-wide kill lever.
 */
export const IS_NATIVE_TAURI_CLIPBOARD_ENABLED = () => {
  return posthog.isFeatureEnabled('release-apps-native-tauri-clipboard-desktop') ?? false;
};

export const IS_PATIENT_CONTEXT_AUTO_EXTRACT_ENABLED = () => {
  return OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getBooleanValue(
    'release-patient-context-auto-extract',
    false
  );
};

export const IS_GPCCMP_ENABLED = () => {
  return posthog.isFeatureEnabled('release-gpccmp');
};

export const IS_AUTO_MODE_ONLY_ENABLED = () => {
  return posthog.isFeatureEnabled('release-auto-mode-only');
};

/**
 * Gates the Dictate history page and its sidebar entry. Requires the
 * platform's dictation rollout (`IS_PLATFORM_DICTATION_ENABLED`).
 * `release-scribe-enable-dictation-log` shipped 100% — removed.
 */
export const IS_DICTATION_LOGS_VISIBLE = () => {
  return IS_PLATFORM_DICTATION_ENABLED();
};

/** Shipped 100% — Flagsmith `release-scribe-enable-dictation-log`. */
export const IS_DICTATION_LOGS_ENABLED = () => {
  return true;
};

/**
 * SCR-3993: gates optional stats on the Dictate history page. ANDs the
 * platform's dictation rollout (`IS_PLATFORM_DICTATION_ENABLED`) so stats
 * never resolve true outside the platform's dictation rollout (SCR-5860).
 * `release-scribe-enable-dictation-log-stats` shipped 100% — removed.
 */
export const IS_DICTATION_LOGS_STATS_ENABLED = () => {
  return IS_PLATFORM_DICTATION_ENABLED();
};

/**
 * Gates the stats section on the Dictate history page. The platform gate now
 * lives in `IS_DICTATION_LOGS_STATS_ENABLED`, so this is a semantic alias.
 */
export const IS_DICTATION_LOGS_STATS_VISIBLE = () => {
  return IS_DICTATION_LOGS_STATS_ENABLED();
};

/** Shipped 100% (Flagsmith). v2 transcription backend when platform dictation is on. */
export const IS_DICTATION_LOGS_BACKEND_ENABLED = () => {
  return IS_PLATFORM_DICTATION_ENABLED();
};

/**
 * Gates the Tone tab — desktop-only + platform dictation rollout.
 * Former Flagsmith gate shipped 100%; keep `isTauri` + platform AND.
 */
export const IS_DICTATION_TONE_SETTINGS_VISIBLE = () => {
  return isTauri() && IS_PLATFORM_DICTATION_ENABLED();
};

export const IS_INTERACTIVE_SESSION_START_ENABLED = () => {
  return posthog.isFeatureEnabled('release-interactive-session-start');
};

export const IS_TEMPLATE_CREATION_RECORD_ENABLED = () => {
  return posthog.isFeatureEnabled('release-template-creation-record');
};

export const IS_HEALTHDIRECT_INTEGRATION_ENABLED = () => {
  return posthog.isFeatureEnabled('release-healthdirect-integration') ?? false;
};

export const IS_SESSIONLESS_EVIDENCE_ENABLED = () => {
  return posthog.isFeatureEnabled('release-sessionless-evidence') ?? false;
};

/**
 * PostHog multivariate for sessionless Evidence awareness popover (GRO-2896).
 * `control`: no popover / not in experiment. Other keys match {@link PersonalizedEvidenceSuggestionCategory}.
 */
export type EvidenceAwarenessPopupVariant = 'control' | 'floating' | 'sidebar';
const evidenceAwarenessPopupVariantsSchema = z
  .enum(['control', 'floating', 'sidebar'])
  .catch('control');

export const GET_EVIDENCE_AWARENESS_POPUP_VARIANT = (): EvidenceAwarenessPopupVariant => {
  if (getEnvironment() !== ENV_NAME.PROD && typeof window !== 'undefined') {
    const devOverride = getDefaultStore().get(
      sessionlessEvidenceAwarenessDevVariantOverrideAtom
    );
    if (devOverride != null) {
      return evidenceAwarenessPopupVariantsSchema.parse(devOverride);
    }
  }
  return evidenceAwarenessPopupVariantsSchema.parse(
    posthog.getFeatureFlag('exp-evidence-awareness-popup')
  );
};

export const IS_EVIDENCE_ACCESS_OVERRIDE_ENABLED = () => {
  return posthog.isFeatureEnabled('release-evidence-access-override') ?? false;
};

export const IS_NHS_EVIDENCE_CONTACT_SALES_ENABLED = () => {
  return posthog.isFeatureEnabled('release-nhs-evidence-contact-sales') ?? false;
};

export const IS_GET_HEIDI_FOR_MOBILE_ENABLED = () => {
  return posthog.isFeatureEnabled('release-get-heidi-for-mobile');
};

export const IS_SEND_GET_MOBILE_EMAIL_ENABLED = () => {
  return posthog.isFeatureEnabled('release-send-get-mobile-email');
};

/** Multivariate shipped 100% → `skip-selection-bottom` (PostHog `side-by-side-skip-selection-button`). */
export const SIDE_BY_SIDE_SKIP_SELECTION_BUTTON_CONFIG = ():
  | 'skip-selection-top'
  | 'skip-selection-bottom'
  | 'disabled' => {
  return 'skip-selection-bottom';
};

/** PostHog feature flag key: desktop (Tauri) dictation rollout. */
export const DESKTOP_DICTATION_FLAG = 'release-desktop-dictation';

/** PostHog feature flag key: web dictation rollout (legacy key name: `universal-dictation`). */
export const WEB_DICTATION_FLAG = 'universal-dictation';

export const IS_WEB_DICTATION_ENABLED = () => {
  return posthog.isFeatureEnabled(WEB_DICTATION_FLAG) ?? false;
};

/**
 * Desktop Dictation: Enables dictation bubble on the Tauri desktop app.
 * Uses Tauri's native microphone permission handling for proper macOS integration.
 *
 * Raw desktop dictation rollout/platform flag.
 */
export const IS_DESKTOP_DICTATION_ENABLED = () => {
  if (!isTauri()) return false;
  return posthog.isFeatureEnabled(DESKTOP_DICTATION_FLAG) ?? false;
};

/**
 * The current platform's dictation rollout gate (desktop: `release-desktop-dictation`,
 * web: `universal-dictation`). Every dictation sub-feature flag should AND with this
 * so the feature never appears unless the user is in the platform's dictation rollout.
 * Non-hook twin of `useIsDictationSettingsEnabled`.
 */
export const IS_PLATFORM_DICTATION_ENABLED = () => {
  return isTauri() ? IS_DESKTOP_DICTATION_ENABLED() : IS_WEB_DICTATION_ENABLED();
};

/**
 * Persists the dictation playback popover's audio position so it resumes from
 * where the user left off when they close and reopen it. Behind a flag so we
 * can disable it remotely if the position-restore behaviour misbehaves.
 */
export const IS_DICTATION_PLAYBACK_RESUME_ENABLED = () => {
  return posthog.isFeatureEnabled('release-dictation-playback-resume') ?? false;
};

export const IS_GDT_INTEGRATION_ENABLED = () => {
  return posthog.isFeatureEnabled('release-gdt-integration') ?? false;
};

export const IS_USER_FORM_TEMPLATE_REQUESTS_ENABLED = () => {
  return posthog.isFeatureEnabled('release-user-form-template-requests');
};

export const IS_SUCCINCT_WORK_EMAIL_HINT_ENABLED = () => {
  return posthog.isFeatureEnabled('release-succinct-work-email-hint');
};

/** PostHog: custom downgrade flows in billing (retention offer / cancel confirmation). */
export const IS_BILLING_CUSTOM_DOWNGRADE_FLOWS_ENABLED = () => {
  return posthog.isFeatureEnabled('release-billing-custom-downgrade-flows-enabled');
};

export const IS_GAMIFY_TRIAL_LENGTH_ENABLED = () => {
  return posthog.isFeatureEnabled('release-gamify-trial-length');
};

export const IS_GAMIFY_TRIAL_LENGTH_CLINICIAN_ENABLED = () => {
  return posthog.isFeatureEnabled('release-gamify-trial-length-clinician');
};

/** PostHog multivariate: CONTROL (un-skippable), OPTIONAL (skippable), NONE (no modal). */
export type ClinicianTrialModalExperimentVariant = 'CONTROL' | 'OPTIONAL' | 'NONE';

export const CLINICIAN_TRIAL_MODAL_EXPERIMENT_FLAG_KEY =
  'exp-scribe-clinician-trial-modal';

/**
 * True when PostHog returned a multivariate variant string (user is in the experiment).
 * `false` / `undefined` / `null` mean the flag is off or the user is outside rollout — not enrolled.
 */
export const IS_CLINICIAN_TRIAL_MODAL_EXPERIMENT_ASSIGNED = (): boolean => {
  const raw = posthog.getFeatureFlag(CLINICIAN_TRIAL_MODAL_EXPERIMENT_FLAG_KEY);
  return typeof raw === 'string';
};

export const GET_CLINICIAN_TRIAL_MODAL_EXPERIMENT_VARIANT =
  (): ClinicianTrialModalExperimentVariant => {
    const schema = z.enum(['CONTROL', 'OPTIONAL', 'NONE']).catch('CONTROL');
    return schema.parse(
      posthog.getFeatureFlag(CLINICIAN_TRIAL_MODAL_EXPERIMENT_FLAG_KEY)
    );
  };

export const IS_GAMIFY_TRIAL_LENGTH_EVIDENCE_ENABLED = () => {
  return posthog.isFeatureEnabled('release-gamify-trial-length-evidence');
};

export const IS_ADD_MORE_SOURCES_ENABLED = () => {
  // FLAG_CLEANUP: hardcoded false because the PostHog flag is no longer configured.
  return false;
};

export const IS_NHS_COMP_V2_ENABLED = () => {
  return posthog.isFeatureEnabled('release-nhs-comp-v2');
};

// Backend-only: ml-scribe gates the create/update template compliance check on
// these keys. Do not add a browser-side posthog-js reader: it evaluates
// independently of the backend and the two can disagree. Do not delete the
// PostHog flags.
export const IS_COMPLIANCE_CHECK_CREATE_TEMPLATE_ENABLED = () => {
  return posthog.isFeatureEnabled('release-compliance-check-create-template');
};

export const IS_COMPLIANCE_CHECK_UPDATE_TEMPLATE_ENABLED = () => {
  return posthog.isFeatureEnabled('release-compliance-check-update-template');
};

export const IS_CHAT_ATTACHMENTS_ENABLED = () => {
  return posthog.isFeatureEnabled('release-chat-attachments');
};

export const IS_CHAT_SUGGESTIONS_ENABLED = () => {
  return posthog.isFeatureEnabled('release-chat-suggestions');
};

export const IS_UK_PRACTICE_SEARCH_ENABLED = () => {
  return posthog.isFeatureEnabled('release-uk-practice-search');
};

export const IS_UK_PRACTICE_SEARCH_MANDATORY_ENABLED = () => {
  return posthog.isFeatureEnabled('release-uk-practice-search-mandatory');
};

export const IS_EVIDENCE_USER_DOCS_ENABLED = () => {
  return posthog.isFeatureEnabled('release-evidence-user-docs');
};

export const IS_EVIDENCE_ONLY_ONBOARDING_ENABLED = () => {
  // FLAG_CLEANUP: hardcoded false because the PostHog flag is no longer configured.
  return false;
};

export const IS_JURISDICTION_IN_ONBOARDING_ENABLED = () => {
  // FLAG_CLEANUP: hardcoded false because the PostHog flag is no longer configured.
  return false;
};

/**
 * Jurisdiction in settings was gated by the evidence onboarding modal (shipped 100%)
 * OR `release-jurisdiction-in-onboarding`. Modal keep-ON ⇒ always available.
 */
export const IS_JURISDICTION_IN_SETTINGS_ENABLED = () => true;

export const IS_EVIDENCE_LIBRARY_ENABLED = () => {
  // Library requires both the library flag AND user docs to be enabled
  return (
    posthog.isFeatureEnabled('release-evidence-library') &&
    IS_EVIDENCE_USER_DOCS_ENABLED()
  );
};

/** Shipped 100% — Flagsmith `release-bulk-upload`. Console flag stays enabled. */
export const IS_BULK_UPLOAD_ENABLED = () => true;

/** PostHog: Evidence activation flow for teams (managed team activation request UI). */
export const IS_EVIDENCE_ACTIVATION_FLOW_FOR_TEAMS_ENABLED = () => {
  return posthog.isFeatureEnabled('release-evidence-activation-flow-for-teams') ?? false;
};

/** PostHog: Team settings Access section — per-member / per-role Evidence access for multi-seat teams. */
export const IS_TEAM_EVIDENCE_ACCESS_CONTROLS_ENABLED = () => {
  return posthog.isFeatureEnabled('release-team-evidence-access-controls') ?? false;
};

/**
 * EVI-1957: Chat source control requires ≥1 Evidence source.
 * Sync read for Jotai atoms (includeWebSearchAtom + toggle writers).
 * Prefer `useIsEvidenceChatRequireAtLeastOneSourceEnabled` from
 * `@/features/flags/evidence-chat-require-at-least-one-source` in React components.
 *
 * Shipped 100% — Flagsmith stays enabled. The key stays on
 * `EVIDENCE_CHAT_REQUIRE_AT_LEAST_ONE_SOURCE_FLAG_KEY`.
 */
export const IS_EVIDENCE_CHAT_REQUIRE_AT_LEAST_ONE_SOURCE_ENABLED = () => true;

/**
 * Clinical web drill-in panel inside source control V2 (guidelines toggle +
 * inline country / jurisdiction / specialty edits). Independent rollout gate so
 * the panel can stay OFF in markets where it must not ship (e.g. EU/UK cohorts in PostHog).
 * Default OFF.
 */
export const IS_SOURCE_CONTROL_CLINICAL_WEB_PANEL_ENABLED = () => {
  return posthog.isFeatureEnabled('release-source-control-clinical-web-panel') ?? false;
};

/**
 * GRO-2897: Personalised source logos inside the Sources button.
 * When enabled, the inline mode icons (Stethoscope / BookOpen / LibraryBig / Link2)
 * are replaced with a stack of actual personalised source logos (integration
 * partners + active web-collection favicons).
 */
export const IS_SOURCE_CONTROL_PERSONALISED_LOGOS_ENABLED = () => {
  return posthog.isFeatureEnabled('release-source-control-personalised-logos') ?? false;
};

/**
 * Unified Collections: merges document collections and source collections
 * into a single "collection" concept across the evidence library.
 *
 * Hard-gated OFF for EU/UK (DEV-12160): the unified-collections UI is going
 * through medical-device certification and must not ship to those regions yet.
 * EU/UK users stay on the legacy Library UI; document uploads are unaffected
 * because they are not gated by this flag.
 *
 * Region resolution here is deliberately compliance-grade: we read the
 * authoritative Kinde region straight from the React Query cache (the same
 * source `useIsUKOrEU` uses) and block the feature for regions on an explicit
 * DENY-LIST (`UNIFIED_COLLECTIONS_BLOCKED_REGIONS`). It still FAILS CLOSED on an
 * unresolved/unknown region (stays on the legacy UI). We intentionally do NOT
 * use `getRegion()` — its own docs warn it fails open to AU and honours the
 * spoofable `?region=` query param, so it must never gate a medical-device
 * decision.
 *
 * `reactQueryClient` is resolved lazily (not a top-level import): `flags.ts` is
 * imported almost everywhere, so adding it to the module-load graph reorders
 * initialisation and trips `reactQueryClient` mocks in unrelated suites (and
 * risks a `@/store` import cycle via the query layer). Requiring at call time
 * keeps this near-leaf module dependency-free at load and is safe because every
 * module is initialised by the time the gate is evaluated.
 */
/**
 * Regions where the unified-collections UI is blocked for medical-device
 * certification (DEV-12160). Every other region receives the feature (still
 * subject to the PostHog flag).
 *
 * `SE_AZ1` (Sweden) is included because Sweden is an EU member state, even
 * though its enum value is neither `UK` nor `EU` — a deny-list keyed on only
 * those two values would wrongly let it through. Any future EU/UK region added
 * to the `Region` enum must be added here too.
 */
const UNIFIED_COLLECTIONS_BLOCKED_REGIONS: ReadonlySet<Region> = new Set([
  Region.UK,
  Region.EU,
  Region.SE_AZ1,
]);

const isUnifiedCollectionsRegionAllowed = (): boolean => {
  /* eslint-disable @typescript-eslint/no-require-imports */
  const { reactQueryClient } =
    require('@/utils/reactQueryClient') as typeof import('@/utils/reactQueryClient');
  /* eslint-enable @typescript-eslint/no-require-imports */

  // Guard against unit-test stubs that partially mock the query client.
  if (typeof reactQueryClient.getQueryData !== 'function') return false;

  // Key mirrors GET_KINDE_PROPERTIES in useQueryKindeProperties.ts; inlined as
  // a literal to avoid pulling that module's `@/store` graph into flags.ts.
  const kindeProperties = reactQueryClient.getQueryData<{ region?: Region }>([
    'getKindeProperties',
  ]);
  const region = kindeProperties?.region;

  // Fail closed on an unknown region (pending/unauthenticated); otherwise allow
  // any region that is not explicitly blocked.
  if (!region) return false;
  return !UNIFIED_COLLECTIONS_BLOCKED_REGIONS.has(region);
};

/**
 * Flagsmith key, deliberately renamed from the PostHog `release-unified-collections`.
 * That key has no PostHog flag row and was hardcoded false in #19285; shipped
 * mobile builds still read it, so creating it would switch the feature on
 * fleet-wide (APP-9911). A distinct key keeps the web rollout independent.
 */
export const UNIFIED_COLLECTIONS_FLAG_KEY = 'release-evidence-unified-collections';

export const IS_UNIFIED_COLLECTIONS_ENABLED = () => {
  if (!isUnifiedCollectionsRegionAllowed()) return false;
  return OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getBooleanValue(
    UNIFIED_COLLECTIONS_FLAG_KEY,
    false
  );
};

export const IS_AUTH0_SOCIAL_LOGIN_APPLE_ENABLED = () => {
  if (process.env.NEXT_PUBLIC_FORCE_KINDE === 'true') return false;
  return posthog.isFeatureEnabled('release-auth-auth0-social-login-apple');
};

/** Show Evidence debug panel when enabled via PostHog */
export const IS_EVIDENCE_DEBUG_PANEL_ENABLED = () => {
  return posthog.isFeatureEnabled('evidence-debug-panel');
};

/**
 * Ask Heidi Shortcuts / Protocols: `/` menu and protocols button in Evidence chat.
 *
 * Independent of `release-transcript-chips` (EVI-2114). Always on: the dedicated
 * PostHog flag was never the production gate (chips-on short-circuited this to
 * true), so killing chips must not suddenly depend on a separate 0% flag.
 */
export const IS_ASK_HEIDI_SHORTCUTS_ENABLED = () => {
  return true;
};

/**
 * Transcript Chips: Real-time Evidence query suggestions during recording.
 * Shows suggested queries based on live transcript content.
 * Kill switch for AI clinical suggestion / red-flag chips — does not gate shortcuts.
 */
export const IS_TRANSCRIPT_CHIPS_ENABLED = () => {
  return posthog.isFeatureEnabled('release-transcript-chips') ?? false;
};

/**
 * Non-US compliance mode for suggestions and shortcuts.
 *
 * When enabled, gates the non-US experience:
 * - Swaps homepage suggestion copy to retrieval-oriented variants
 * - Disables AI-generated clinical suggestion chips
 * - Filters the shortcut library to remove autonomous Dx/Ix/Mx advice
 *
 * This flag controls the rollout; the actual market check uses isUSMarket().
 */
export const IS_NON_US_SUGGESTIONS_COMPLIANCE_ENABLED = () => {
  // FLAG_CLEANUP: hardcoded false because the PostHog flag is no longer configured.
  return false;
};

/**
 * Floating Ask Heidi Input
 *
 * Enables the new floating Ask Heidi input that appears at the bottom of session pages.
 * Features a pill-shaped input with thread selector, voice input, and choreographed
 * animations when opening the chat sidebar.
 *
 * Independent of `release-transcript-chips` (EVI-2114). Always on for the same
 * reason as shortcuts — chips-on previously short-circuited this gate, and killing
 * chips must not fall back to AskAIV2 and drop the shortcuts surface.
 */
export const IS_FLOATING_ASK_HEIDI_ENABLED = () => {
  return true;
};

export const SHOULD_SHOW_AUTO_ANSWER_TOGGLE = () => {
  return false;
};

export const IS_DOMAIN_REWRITE_ENABLED = () => {
  return posthog.isFeatureEnabled('release-domain-rewrite');
};

/**
 * Ask Heidi History: Shows chat history dropdown in session header.
 * Allows users to view and switch between past Ask Heidi chats for the current session.
 * Independent of `release-transcript-chips` (EVI-2114).
 */
export const IS_ASK_HEIDI_HISTORY_ENABLED = () => {
  if (!IS_ASK_HEIDI_V2_CHAT_COMPLETIONS_ENABLED()) {
    return false;
  }
  return posthog.isFeatureEnabled('release-ask-heidi-history') ?? true;
};

/**
 * Link Patients to Out-of-Session Evidence: Enables "/" and "@" triggers in Evidence chat.
 * Allows users to link sessions (/) and patients (@) to their Evidence queries
 * when not in a session context.
 */
export const IS_LINK_PATIENTS_TO_OUT_OF_SESSION = () => {
  return posthog.isFeatureEnabled('release-link-patients-to-out-of-session');
};

/**
 * Linked Sessions Auto-Select: When enabled, auto-selects recent sessions from the
 * same patient profile as fallback linked sessions for context.
 * When disabled, users must manually select linked sessions.
 */
export const IS_LINKED_SESSIONS_AUTO_SELECT_ENABLED = () => {
  return posthog.isFeatureEnabled('release-linked-sessions-auto-select') ?? false;
};

/**
 * Template Library Tabs: Redesigned template library with integrated Favourites tab.
 * When enabled, shows a unified tab system with Favourites as a tab (card layout)
 * instead of a separate section. Also includes full-width search and Uses column.
 */
export const IS_TEMPLATE_LIBRARY_TABS_ENABLED = () => {
  return posthog.isFeatureEnabled('release-template-library-tabs');
};

/**
 * The one flag that picks the onboarding flow (`release-new-onboarding-flow`).
 * Every variant is a complete flow — there are no sub-flags underneath:
 *
 * - `control`        → the legacy single-form sign-up.
 * - `experimental-2` → the sample-conversation flow (current production).
 * - `experimental-3` → the guided tour: the sample-conversation flow with the
 *                      note-edit accordion split into three tour screens and
 *                      the team invite moved ahead of transcription.
 *
 * The retired `experimental` value (the v2 template-creation flow) parses as
 * `control`, like any other unknown value.
 */
export type OnboardingV2Variant = 'control' | 'experimental-2' | 'experimental-3';

export const ONBOARDING_FLOW_FLAG_KEY = 'release-new-onboarding-flow';

// PostHog is the source of truth for pre-auth market eligibility and rollout.
// A missing or unmatched flag resolves to `control`.

export const IS_ONBOARDING_V2_ENABLED = () => {
  if (isTauri()) return false;
  return GET_ONBOARDING_V2_VARIANT() !== 'control';
};

export const GET_ONBOARDING_V2_VARIANT = (): OnboardingV2Variant => {
  if (isTauri()) return 'control';
  const schema = z.enum(['control', 'experimental-2', 'experimental-3']).catch('control');
  const variant = schema.parse(posthog.getFeatureFlag(ONBOARDING_FLOW_FLAG_KEY));

  if (variant !== 'control') {
    // Both treatment flows are desktop-web only.
    if (typeof window === 'undefined' || isMobile) return 'control';
  }

  return variant;
};

/** True for any onboarding-v2 treatment (`experimental-2` or `experimental-3`). */
export const IS_ONBOARDING_V2_TREATMENT = () => {
  if (!IS_ONBOARDING_V2_ENABLED()) return false;
  return GET_ONBOARDING_V2_VARIANT() !== 'control';
};

export const IS_EARLY_MEETING_FLOW_ENABLED = () => {
  return posthog.isFeatureEnabled('release-early-meeting-flow');
};

/**
 * When true, surfaces that would render the V2 `<SignUpPage>` OR the legacy
 * `<UnauthenticatedDemoFlow>` instead bypass them and redirect straight to
 * the Auth0 register screen. Variant-agnostic by design — works regardless
 * of `release-new-onboarding-flow` rollout state, so the marketing-attributed
 * `/onboarding` entry funnels every visitor to auth without waiting for V2
 * to roll out.
 *
 * Forced off outside cloud web, regardless of PostHog rollout: the Auth0
 * browser redirect is unsafe under the Tauri auth plugin, and the on-prem
 * profile authenticates against Keycloak, not Auth0. On-prem is checked
 * separately from Tauri because the profile can also be served as web, where
 * `isTauri()` is false.
 */
export const IS_ONBOARDING_SIGNUP_BYPASS_ENABLED = () => {
  if (isTauri() || isOnPrem) return false;
  return posthog.isFeatureEnabled('release-onboarding-signup-bypass');
};

export const IS_CHANGE_EMAIL_ENABLED = () => {
  return posthog.isFeatureEnabled('release-change-email');
};

/**
 * Sidebar Referral Button: Shows a "Refer and earn" button in the sidebar for paid users.
 */
export const IS_SIDEBAR_REFERRAL_BUTTON_ENABLED = () => {
  return posthog.isFeatureEnabled('release-sidebar-referral-button');
};

export const IS_HIDE_SESSION_UPGRADE_BUTTON_EVIDENCE_FAKE_TRIALS_ENABLED = () => {
  // FLAG_CLEANUP: hardcoded false because the PostHog flag is no longer configured.
  return false;
};

export const IS_EVIDENCE_NAVIGATION_ENABLED = () => {
  return posthog.isFeatureEnabled('release-evidence-navigation');
};

/**
 * When enabled, users will enter Evidence trial flow immediately upon signup through the public chat page.
 * Also enables new trial and upgrade flows for new clinician, practice plans.
 * designs: https://www.figma.com/design/MSqFiRMYvicVZlaiWjmFG3/Trials---upgrades?node-id=7112-8816&m=dev
 */
export const IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED = () => {
  return posthog.isFeatureEnabled('release-evidence-launch-trial-and-upgrade-flows');
};

/**
 * When enabled, shows the redesigned billing page in Evidence settings.
 */
export const IS_EVIDENCE_LAUNCH_BILLING_PAGE_REDESIGN_ENABLED = () => {
  return posthog.isFeatureEnabled('release-evidence-launch-billing-page-redesign');
};

/**
 * When enabled, shows a contextual upgrade modal to power users of Evidence during their
 * Clinician trial, personalised with their Evidence usage count.
 * designs: https://www.figma.com/design/MSqFiRMYvicVZlaiWjmFG3/Trials---upgrades?node-id=9562-47495&m=dev
 */
export const IS_EVIDENCE_POWER_USER_UPGRADE_MODAL_ENABLED = () => {
  // FLAG_CLEANUP: hardcoded false because the PostHog flag is no longer configured.
  return false;
};

/** AGE-1455: routines desktop nurture banner on Heidi Work web home. */
export const IS_WORK_DESKTOP_NURTURE_BANNER_ENABLED = () => {
  // FLAG_CLEANUP: hardcoded false because the PostHog flag is no longer configured.
  return false;
};

export const IS_RPA_DEV_PLAYGROUND_ENABLED = () => {
  if (!isTauri()) return false;
  if (getEnvironment() === ENV_NAME.PROD) return false;
  return process.env.NEXT_PUBLIC_DEV_DASHBOARD === 'true';
};

/**
 * Feature flag for the Evidence experience feedback nudge.
 * Shows a feedback prompt after 3-10 queries asking "How useful is Evidence for you?"
 */
export const IS_EVIDENCE_EXPERIENCE_FEEDBACK_ENABLED = () => {
  return posthog.isFeatureEnabled('release-evidence-experience-feedback');
};

/**
 * Ribbon Sidebar: When enabled, uses the 'label-icons' sidebar variant
 * (compact logo, footer/referral/trial layout) and replaces the static
 * "New Session" button with the global create action dropdown.
 *
 * NOTE: React render sites should prefer the reactive `useIsRibbonSidebarEnabled`
 * hook in `@/features/flags`, which additively folds in the new-session-list
 * master flag. This synchronous helper reads ONLY the raw `release-sidebar-redesign`
 * PostHog flag and exists for non-React callers (and as the guard for
 * IS_SIDEBAR_PANEL_ENABLED below, which is intentionally NOT part of the
 * new-session-list rollout).
 */
export const IS_RIBBON_SIDEBAR_ENABLED = () => {
  return posthog.isFeatureEnabled('release-sidebar-redesign') ?? false;
};

/**
 * Sidebar Panel: renders the hover/pin side panel that hosts the sessions
 * list and evidence chat. Nav items reveal panel content on hover. Moves
 * sessions/evidence out of ResizableMenuPanels and ChatSidebar.
 *
 * Requires IS_RIBBON_SIDEBAR_ENABLED — the panel is designed for the ribbon's
 * label-icons sidebar width and flat IA. Enabling 'release-page-shell-panels'
 * in PostHog without the ribbon flag has no effect.
 *
 * The new session list and the sidebar panel are mutually exclusive layouts.
 * `release-scribe-enable-new-session-list` (the master flag, on Flagsmith /
 * OpenFeature) takes precedence: whenever it is ON, the panel is forced OFF —
 * regardless of the ribbon / 'release-page-shell-panels' flags. This mirrors,
 * inverted, how `useIsRibbonSidebarEnabled` additively folds new-session-list
 * in to ENABLE the ribbon.
 *
 * Agent Home owns the same secondary-panel slot. It is nav-v3 AND Heidi Work
 * (the Heidi II master flag, or the platform Projects flag). When that is on,
 * the panel is forced OFF here — not in a second hook — so layout and the
 * legacy list's own chrome cannot disagree.
 */
export const IS_SIDEBAR_PANEL_ENABLED = () => {
  // new-session-list wins; force the panel off before checking its own flags.
  // Read synchronously from Flagsmith/OpenFeature (the master flag's provider),
  // alongside the PostHog read below — same dual-provider shape as
  // IS_PATIENT_CONTEXT_AUTO_EXTRACT_ENABLED.
  const isNewSessionListEnabled = OpenFeature.getClient(
    FEATURE_FLAG_DOMAIN
  ).getBooleanValue(newSessionListFlag.key, false);
  if (isNewSessionListEnabled) return false;
  if (isAgentHomeV1Enabled()) return false;
  if (!IS_RIBBON_SIDEBAR_ENABLED()) return false;
  return posthog.isFeatureEnabled('release-page-shell-panels') ?? false;
};

/**
 * White-label branding for R1 partner.
 * When enabled, users can access /p/r1 to activate R1 branding.
 */
export const IS_WHITE_LABEL_R1_ENABLED = () => {
  // FLAG_CLEANUP: hardcoded false because the PostHog flag is no longer configured.
  return false;
};

/**
 * Evidence Leaderboard: Shows the Evidence leaderboard and referral features.
 * When enabled, users can see their ranking, view leaderboards, and share referral links.
 * Appears in: Settings > Account, Sidebar "Refer & Earn" CTA, /leaderboard page.
 */
export const IS_EVIDENCE_LEADERBOARD_ENABLED = () => {
  return posthog.isFeatureEnabled('release-evidence-leaderboard');
};

export const IS_CONSULT_NOTE_EDIT_DISPLAY_ENABLED = () => {
  return posthog.isFeatureEnabled('release-consult-note-edit-display');
};

export const IS_ASSISTANT_VIEW_ENABLED = () => {
  return posthog.isFeatureEnabled('release-assistant-view');
};

/**
 * Evidence-only Referral: When enabled, Evidence-only subscribers (users with active
 * Evidence Pro but no Scribe subscription) see alternate referral copy ($25 instead of $50)
 * and are routed to a separate Rewardful campaign.
 */
export const IS_EVIDENCE_ONLY_REFERRAL_ENABLED = () => {
  return posthog.isFeatureEnabled('release-evidence-only-referral');
};

/**
 * Whether the current user signed up via dictate. Reads the cached user from
 * the query client lazily (mirrors `getCurrentUser` in useQueryUser.ts); a
 * top-level import of that module pulls `@/features/flags` back into `flags.ts`
 * and forms an init cycle that breaks unrelated suites.
 */
const isDictateSignup = (): boolean => {
  /* eslint-disable @typescript-eslint/no-require-imports */
  const { reactQueryClient } =
    require('@/utils/reactQueryClient') as typeof import('@/utils/reactQueryClient');
  /* eslint-enable @typescript-eslint/no-require-imports */

  if (typeof reactQueryClient.getQueryData !== 'function') return false;

  // Key mirrors GET_USER in useQueryUser.ts; inlined as a literal to keep that
  // module's import graph out of flags.ts.
  const user = reactQueryClient.getQueryData<{ signup_source?: SignupSource }>([
    'getUser',
  ]);
  return user?.signup_source === SignupSource.dictate;
};

/**
 * Demo session flow shown after onboarding (5 coach marks).
 * Guides new users through a demo transcription with coach marks.
 * Requires Evidence launch flows and transcript chips to be enabled.
 * English-only for now.
 */
export const IS_DEMO_SESSION_ENABLED = () => {
  if (!isEnglishMarket()) return false;
  if (!IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED() && !isDictateSignup())
    return false;
  if (!IS_TRANSCRIPT_CHIPS_ENABLED()) return false;
  return posthog.isFeatureEnabled('release-demo-session');
};

/**
 * Lite demo session flow with 3 coach marks (refine note, create docs, new session).
 * Fallback when transcript chips are off: requires Evidence launch flows but not
 * transcript chips. Only activates when the full demo session flag is OFF.
 * English-only for now.
 */
export const IS_DEMO_SESSION_LITE_ENABLED = () => {
  if (!isEnglishMarket()) return false;
  if (!IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED() && !isDictateSignup())
    return false;
  if (IS_DEMO_SESSION_ENABLED()) return false;
  return posthog.isFeatureEnabled('release-demo-session-lite');
};

/**
 * Controls when the evidence sidebar auto-opens relative to transcription:
 * - 'control': do nothing
 * - 'after-transcription': open as soon as transcription is completed
 * - 'during-transcription': open during transcription (when transcription starts)
 */
export const GET_EVIDENCE_SIDEBAR_AUTO_OPEN_VARIANT = ():
  | 'control'
  | 'after-transcription'
  | 'during-transcription' => {
  const schema = z
    .enum(['control', 'after-transcription', 'during-transcription'])
    .catch('control');
  return schema.parse(posthog.getFeatureFlag('experiment-evidence-sidebar-auto-open'));
};

// START Integration migration flags
const IntegrationMigrationFlags: Partial<Record<EHRProvider, string>> = {
  [EHRProvider.Epic_Systems]: 'release-epic-new-framework',
  [EHRProvider.Mindbody]: 'release-mindbody-new-framework',
  [EHRProvider.Best_Practice]: 'release-best-practice-new-framework',
};

export const HAS_OLD_FRAMEWORK_SUPPORT = (ehrProvider: EHRProvider) =>
  Boolean(IntegrationMigrationFlags[ehrProvider]);

export const IS_INTEGRATION_NEW_FRAMEWORK_ENABLED = (ehrProvider: EHRProvider) => {
  const integrationMigrationFlag = IntegrationMigrationFlags[ehrProvider];

  // For integrations that don't have a migration flag (i.e. were never on old framework), use the new framework
  if (!integrationMigrationFlag) return true;

  if (ehrProvider === EHRProvider.Epic_Systems) {
    // FLAG_CLEANUP: hardcoded false because the PostHog flag is no longer configured.
    return false;
  }

  return posthog.isFeatureEnabled(integrationMigrationFlag) ?? false;
};

// END Integration migration flags

/**
 * Slide Builder: Dedicated split-pane UI for creating and editing presentations.
 * Left panel shows slide editor with thumbnails, right panel embeds Evidence chat
 * scoped to the selected slide for per-slide AI editing.
 */
export const IS_SLIDE_BUILDER_ENABLED = () => {
  return posthog.isFeatureEnabled('release-slide-builder') ?? false;
};

export const IS_EXPORT_QR_CODE_ENABLED = () => {
  return posthog.isFeatureEnabled('release-export-qr-code');
};

export const IS_EVIDENCE_SBS_ENABLED = () => {
  return posthog.isFeatureEnabled('release-evidence-sbs') ?? false;
};

export const IS_TEMPLATE_BUILDER_SESSION_TRANSCRIPT_PICKER_ENABLED = () => {
  return posthog.isFeatureEnabled('release-template-builder-session-transcript-picker');
};

/**
 * Inset main-content design treatment.
 *
 * Additively folds in the `release-scribe-enable-new-session-list` master flag
 * (Flagsmith / OpenFeature): whenever new-session-list is ON the inset design is
 * forced ON, regardless of the raw `release-inset-design` PostHog flag. The new
 * session list layout requires the inset treatment, so this mirrors how
 * `useIsRibbonSidebarEnabled` folds new-session-list in to enable the ribbon
 * sidebar.
 *
 * Read synchronously from both providers (PostHog + Flagsmith/OpenFeature),
 * matching the dual-provider shape of IS_SIDEBAR_PANEL_ENABLED above.
 */
export const IS_INSET_DESIGN_ENABLED = () => {
  const isNewSessionListEnabled = OpenFeature.getClient(
    FEATURE_FLAG_DOMAIN
  ).getBooleanValue(newSessionListFlag.key, false);
  return (
    (posthog.isFeatureEnabled('release-inset-design') ?? false) || isNewSessionListEnabled
  );
};

export const IS_END_OF_90_DAY_TRIAL_EXPERIENCE_ENABLED = () => {
  return posthog.isFeatureEnabled('release-end-of-90-day-trial-experience');
};

/** Gates chat capacity guard UI: warning/critical/exceeded banners, input blocking, and continue-in-new-chat. */
export const IS_CHAT_CAPACITY_GUARD_ENABLED = () =>
  OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getBooleanValue(
    'release-evidence-chat-capacity-guard',
    false
  );

/** EVI-1493 / EVI-2477: Progressive streaming timeout (15s warning, 30s abort) instead of single 5-min hard timeout. */
export const IS_PROGRESSIVE_STREAM_TIMEOUT_ENABLED = () =>
  OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getBooleanValue(
    'release-evidence-progressive-stream-timeout',
    false
  );

/**
 * DEV-12893 / SWR-171 / EVI-2557: when the browser fires `offline` mid-stream,
 * abort and skip recovery instead of waiting out the timeout. Does not block
 * the send on `navigator.onLine` — Chrome can report offline while the tab is
 * still reaching the API. A real transport failure still surfaces the
 * connectivity notice after the request. Default ON — set
 * `release-evidence-offline-fail-fast` to false to leave the stream running
 * through an `offline` event.
 */
export const IS_EVIDENCE_OFFLINE_FAIL_FAST_ENABLED = () =>
  OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getBooleanValue(
    'release-evidence-offline-fail-fast',
    true
  );

/**
 * Kill switch for the clipboard write recovery: the synchronous native write on
 * Safari/WebKit and, on the polyfill path, the direct native attempt (with its
 * bounded hold for an unfocused window) that runs once the retries are spent.
 * Flip ON to skip both without a revert — which is the only way to reach a
 * desktop bundle that has already shipped. It does NOT disable the Tauri
 * OS-clipboard rescue: that still runs after the retries on desktop, so a
 * killed recovery is the polyfill plus that rescue, not the polyfill alone.
 * Non-hook so the pure clipboard helper can read it imperatively.
 */
export const isClipboardWriteRecoveryKilled = () =>
  OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getBooleanValue(
    'kill-scribe-copy-clipboard-recovery',
    false
  );

/**
 * Gates the LOAD-BEARING browser-side `signup_conversion` marketing event (the
 * Meta conversion pair with server-side `sign_up_successful`). Off by default.
 * This is the SINGLE gate for the event — it controls rollout/kill-switch and
 * environment scoping: enable it ONLY in the production Flagsmith environment so
 * staging/dev never forward signups to Meta. See
 * `analyticsScribeModel.trackSignUpSuccessfulWebAuth`.
 */
export const IS_SIGNUP_CONVERSION_EVENT_ENABLED = () =>
  OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getBooleanValue(
    'op-signup-conversion-event',
    false
  );
